import { useEffect, useState } from 'react'
import { ChevronDown, Check, Plus, Building2, Settings } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useWorkspaces, useCreateWorkspace } from '@/hooks/useWorkspaces'
import { useSwitchWorkspace } from '@/hooks/useSwitchWorkspace'
import { useOrgId } from '@/hooks/useOrgId'
import type { Workspace } from '@/types/workspace'

interface WorkspaceSwitcherProps {
  isCollapsed: boolean
}

/** "My Big Idea!" -> "my-big-idea" */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
}

/**
 * The single workspace control for the whole app.
 *
 * Everything downstream — the project lists on ~40 pages, every
 * workspace-scoped backend route (via the X-Workspace-Id header apiFetch
 * stamps from this store) — follows what is picked here. The duplicate
 * switchers that used to live in the Build top bar and the Brain page header
 * were removed in favour of this one (2026-09-05).
 */
export function WorkspaceSwitcher({ isCollapsed }: WorkspaceSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)

  const navigate = useNavigate()
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId)
  const storeOrgId = useWorkspaceStore((s) => s.activeOrgId)
  const resolvedOrgId = useOrgId()
  const orgId = storeOrgId ?? resolvedOrgId ?? ''

  const switchWorkspace = useSwitchWorkspace()
  const { data } = useWorkspaces(orgId)
  const createWorkspace = useCreateWorkspace(orgId)

  const workspaces = data?.workspaces ?? []
  const active = workspaces.find((w) => w.id === activeWorkspaceId) ?? workspaces[0]

  // Publish the resolved name so workspace-scoped empty states can name the
  // workspace. The sidebar is mounted on every authenticated page, so this is
  // the one place that reliably knows it.
  const setActiveWorkspaceName = useWorkspaceStore((s) => s.setActiveWorkspaceName)
  useEffect(() => {
    if (active?.name) setActiveWorkspaceName(active.name)
  }, [active?.name, setActiveWorkspaceName])

  // Adopt a default when nothing is selected. `useSwitchOrg` clears the
  // workspace on an org switch (a workspace belongs to exactly one org), and
  // nothing else re-resolves it mid-session — AuthProvider only initializes
  // the workspace during its user-data load, which does not re-run on a
  // switch. Without this the store and the backend disagree about what is
  // selected: requests carry no X-Workspace-Id and are correctly scoped to
  // the new org's default, but the sidebar shows no tick and every
  // workspace-scoped empty state loses its label.
  //
  // `setActiveWorkspace`, not `switchWorkspace`: we are adopting the scope
  // we are ALREADY being served, so there is nothing stale to purge. Guarded
  // on "nothing selected" — the same rule AuthProvider uses. Adopting
  // unconditionally is what clobbered manual selections on 2026-09-02.
  const setActiveWorkspace = useWorkspaceStore((s) => s.setActiveWorkspace)
  useEffect(() => {
    if (!activeWorkspaceId && active?.id) setActiveWorkspace(active.id)
  }, [activeWorkspaceId, active?.id, setActiveWorkspace])

  // Render whenever we know the org. The old `workspaces.length <= 1` bail hid
  // the control from exactly the people who needed it — with one workspace
  // there was no way to reach "New workspace" at all.
  if (!orgId) return null

  const handleSelect = (workspace: Workspace) => {
    // Pass the workspace's org so the two stored scopes move together —
    // the backend derives request.state.org_id from the selected
    // workspace, so a lagging activeOrgId means a stale X-Org-Id header.
    switchWorkspace(workspace.id, workspace.org_id)
    setIsOpen(false)
  }

  const openCreate = () => {
    setIsOpen(false)
    setNewName('')
    setCreateError(null)
    setCreateOpen(true)
  }

  const handleCreate = async () => {
    const name = newName.trim()
    if (!name) return
    setCreateError(null)
    try {
      const res = await createWorkspace.mutateAsync({ name, slug: slugify(name) })
      setCreateOpen(false)
      setNewName('')
      // Land the user in the workspace they just made — creating one and
      // staying put is never what anyone means.
      if (res?.workspace?.id) switchWorkspace(res.workspace.id, res.workspace.org_id)
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Could not create workspace.')
    }
  }

  const menu = (
    <div
      role="listbox"
      aria-label="Available workspaces"
      className="absolute left-3 right-3 top-full mt-1 z-50 bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl overflow-hidden"
    >
      {workspaces.map((ws) => (
        <button
          key={ws.id}
          role="option"
          aria-selected={ws.id === activeWorkspaceId}
          onClick={() => handleSelect(ws)}
          className="flex items-center w-full gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
        >
          <span className="flex-1 truncate text-left">{ws.name}</span>
          {ws.id === activeWorkspaceId && (
            <Check className="w-3.5 h-3.5 text-lime" aria-hidden="true" />
          )}
        </button>
      ))}
      <div className="border-t border-white/10">
        <button
          onClick={openCreate}
          aria-label="Create new workspace"
          className="flex items-center w-full gap-2 px-3 py-2 text-sm text-gray-400 hover:bg-white/5 hover:text-white transition-colors"
        >
          <Plus className="w-3.5 h-3.5" aria-hidden="true" />
          <span>New workspace</span>
        </button>
        <button
          onClick={() => {
            setIsOpen(false)
            navigate('/settings?tab=workspaces')
          }}
          aria-label="Manage workspaces"
          className="flex items-center w-full gap-2 px-3 py-2 text-sm text-gray-400 hover:bg-white/5 hover:text-white transition-colors"
        >
          <Settings className="w-3.5 h-3.5" aria-hidden="true" />
          <span>Manage workspaces…</span>
        </button>
      </div>
    </div>
  )

  const createDialog = (
    <Dialog open={createOpen} onOpenChange={setCreateOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New workspace</DialogTitle>
          <DialogDescription>
            Workspaces keep projects, apps and marketing separate. You can switch
            between them any time from the sidebar.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <label className="text-sm text-gray-400" htmlFor="workspace-name">
              Workspace name
            </label>
            <Input
              id="workspace-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newName.trim()) handleCreate()
              }}
              placeholder="Growth"
              autoFocus
            />
          </div>
          {createError && (
            <p role="alert" className="text-xs text-red-400">
              {createError}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="gradient"
              onClick={handleCreate}
              disabled={!newName.trim() || createWorkspace.isPending}
            >
              {createWorkspace.isPending ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )

  if (isCollapsed) {
    return (
      <div className="relative px-3 py-2" data-testid="workspace-switcher">
        <button
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Switch workspace"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          title={active?.name ?? 'Select workspace'}
          className="flex items-center justify-center w-full p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
        >
          <Building2 className="w-4 h-4" aria-hidden="true" />
        </button>
        {isOpen && menu}
        {createDialog}
      </div>
    )
  }

  return (
    <div className="relative px-3 py-2" data-testid="workspace-switcher">
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`Current workspace: ${active?.name ?? 'Select workspace'}`}
        className="flex items-center w-full gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors text-left"
      >
        <Building2 className="w-4 h-4 text-lime shrink-0" aria-hidden="true" />
        <span className="flex-1 text-sm text-white truncate">
          {active?.name ?? 'Select workspace'}
        </span>
        <ChevronDown
          className={cn('w-3.5 h-3.5 text-gray-400 transition-transform', isOpen && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      {isOpen && menu}
      {createDialog}
    </div>
  )
}
